// src/components/Settings.js
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  Box,
  Button,
  Grid,
  Divider,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LogoutIcon from '@mui/icons-material/Logout';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportIcon from '@mui/icons-material/Support';
import axios from 'axios';
import { getFunctions, httpsCallable } from 'firebase/functions';

import PaymentResultPopup from '../components/PaymentResultPopup';
import * as amplitude from '@amplitude/analytics-browser';

const formatRemainingTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0 վայրկյան';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let formattedTime = '';
  if (hours > 0) formattedTime += `${hours}ժ `;
  if (minutes > 0 || (hours > 0 && secs > 0)) formattedTime += `${minutes}ր `;
  if (secs > 0 || (!hours && !minutes)) formattedTime += `${Math.floor(secs)}վրկ`;

  return formattedTime.trim();
};

const Settings = () => {
  const functions = getFunctions();
  const createOneTimePurchaseSession = httpsCallable(functions, 'createOneTimePurchaseSession');

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [oneTimePurchaseLoading, setOneTimePurchaseLoading] = useState(null);
  const [buyTimeDialogOpen, setBuyTimeDialogOpen] = useState(false);
  const [buyGenerationsDialogOpen, setBuyGenerationsDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // One-time purchase options
  const oneTimePurchases = [
    { id: 'price_1StyCJGptgEY0Ls0C8Ar1GgY', hours: 10, price: 50, label: '10 ժամ' },
    { id: 'price_1StyCOGptgEY0Ls0FpW9nyn9', hours: 20, price: 90, label: '20 ժամ', highlighted: true },
    { id: 'price_1StyCRGptgEY0Ls0vOpf146J', hours: 50, price: 200, label: '50 ժամ' },
  ];

  const oneTimeTTSPurchases = [
    { id: 'price_1Syd8YGptgEY0Ls0SkGaCjRD', value: '150 կրեդիտ', price: 15, label: '150 կրեդիտ', type: 'tts' },
    { id: 'price_1Syd8lGptgEY0Ls0huNBpauO', value: '300 կրեդիտ', price: 30, label: '300 կրեդիտ', highlighted: true, type: 'tts' },
    { id: 'price_1Syd92GptgEY0Ls0O43bQx3T', value: '800 կրեդիտ', price: 60, label: '800 կրեդիտ', type: 'tts' },
  ];

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          setUserData(docSnapshot.data());
          setLoading(false);
        } else {
          console.error('User document does not exist');
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, []);

  const handleConnectTelegram = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        let token = userData?.telegramToken;

        if (!token) {
          token = uuidv4();
          await updateDoc(userRef, { telegramToken: token });
        }

        const botLink = `https://t.me/xosumam_bot?start=${token}`;
        window.location.href = botLink;
      } catch (error) {
        console.error('Error creating Telegram token:', error);
      }
    }
  };

  const handleManageSubscription = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        setLoadingPortal(true);
        const idToken = await user.getIdToken();

        // Use the correct API URL - make sure this matches your Firebase function URL
        const apiUrl = 'https://us-central1-xosumam-26040.cloudfunctions.net/createStripeCustomerPortal';

        const response = await axios.post(
          apiUrl,
          {},
          {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          }
        );

        if (response.data && response.data.url) {
          // Redirect to Stripe Customer Portal with return URL to settings page
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error('Error opening customer portal:', error);
        alert('Սխալ տեղի ունեցավ: Խնդրում ենք փորձեք կրկին');
      } finally {
        setLoadingPortal(false);
      }
    }
  };

  const handleOneTimePurchase = async (priceId) => {
    setOneTimePurchaseLoading(priceId);
    try {
      const result = await createOneTimePurchaseSession({
        priceId,
        origin: window.location.origin
      });
      window.location.href = result.data.url;
    } catch (error) {
      console.error('Error creating one-time purchase session:', error);
      setOneTimePurchaseLoading(null);
      alert('Սխալ տեղի ունեցավ: Խնդրում ենք փորձեք կրկին');
    }
  };

  const handleEmailNotificationToggle = async (event) => {
    const enabled = event.target.checked;
    amplitude.track('Email Notification Setting Toggled', { enabled });
    const user = auth.currentUser;
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { emailNotifications_transcriptReady: enabled });
    }
  };

  const getPlanTranslation = (plan, remainingSeconds) => {
    if (!plan || plan === 'free') {
      return remainingSeconds > 300 ? 'Անվճար պլան ավելացված ժամանակով' : 'Անվճար';
    }

    switch (plan) {
      case 'Pro_monthly':
        return 'Pro (ամսական)';
      case 'Pro_yearly':
        return 'Pro (տարեկան)';
      case 'business':
      case 'Business':
        return 'Business';
      case 'business_test':
      case 'Business_test':
        return 'Business Test';
      default:
        return plan;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PaymentResultPopup />

      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 4
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            p: 3,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: 3
            }}
          >
            <Avatar
              src={userData?.photoURL || ''}
              alt={userData?.displayName || 'Օգտատեր'}
              sx={{
                width: 90,
                height: 90,
                border: '4px solid white',
                boxShadow: 2
              }}
            />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {userData?.displayName || 'Անանուն'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                {userData?.email}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => signOut(auth)}
            sx={{
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Դուրս գալ
          </Button>
        </Box>

        {/* Subscription Info */}
        <Box p={3}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="primary" />
            Բաժանորդագրության տվյալներ
          </Typography>

          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                    Ընթացիկ պլան
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 2 }}>
                    {getPlanTranslation(userData?.plan, userData?.remaining_seconds)}
                  </Typography>

                  {(userData?.plan === 'Pro_monthly' || userData?.plan === 'Pro_yearly') && (
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      startIcon={loadingPortal ? null : <CreditCardIcon />}
                      onClick={handleManageSubscription}
                      disabled={loadingPortal}
                      sx={{ mt: 1 }}
                    >
                      {loadingPortal ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                          Բեռնվում է...
                        </Box>
                      ) : (
                        'Կառավարել բաժանորդագրությունը'
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                      Մնացել է
                    </Typography>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {formatRemainingTime(userData?.remaining_seconds)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    fullWidth
                    onClick={() => setBuyTimeDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Գնել ժամանակ
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                      TTS կրեդիտներ
                    </Typography>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {userData?.remaining_tts_generations !== undefined ? userData.remaining_tts_generations : (userData?.plan === 'free' ? 10 : 200)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {userData?.plan === 'free' ? 'Ընդհանուր մնացորդ' : 'Այս ամսվա մնացորդ'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    fullWidth
                    onClick={() => setBuyGenerationsDialogOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Գնել կրեդիտներ
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>


          {/* Support Note - Moved higher and made more prominent */}
          <Card
            sx={{
              bgcolor: 'background.default',
              mt: 2,
              mb: 3,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <SupportIcon color="primary" fontSize="large" />
            <Typography>
              Աջակցության համար՝ <Link href="mailto:support@xosum.am" fontWeight="medium">support@xosum.am</Link>
            </Typography>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Telegram Connection */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TelegramIcon color="primary" />
            Տելեգրամ
          </Typography>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              {userData?.telegramId ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#0088cc', width: 48, height: 48 }}>
                      <TelegramIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Տելեգրամը կապված է
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Username: {userData.telegramUserName || 'անհայտ'}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">
                    Դուք կարող եք ուղարկել ձայնային հաղորդագրություններ մեր բոտին՝ դրանք ավտոմատ կերպով վերծանելու համար:
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'start' : 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#0088cc', width: 48, height: 48 }}>
                      <TelegramIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Կապեք Տելեգրամը
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Տելեգրամով ձայնայինները վերծանելու համար
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<TelegramIcon />}
                    onClick={handleConnectTelegram}
                    sx={{ mt: 1 }}
                  >
                    Կապել Տելեգրամը
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Email Notifications */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Ծանուցումներ
          </Typography>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={userData?.emailNotifications_transcriptReady !== false}
                    onChange={handleEmailNotificationToggle}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Էլ. փոստի ծանուցում վերծանման ավարտի մասին
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ստանալ էլ. նամակ, երբ վերծանումն ավարտված է
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

        </Box>
      </Paper >

      {/* Buy More Time Dialog */}
      <Dialog
        open={buyTimeDialogOpen}
        onClose={() => setBuyTimeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          <Typography variant="h5" fontWeight="bold">
            Գնել վերծանման ժամանակ
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            px: 3
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexShrink: 0 }}>
            Գնեք լրացուցիչ վերծանման ժամանակ, որը կավելացվի ձեր ընթացիկ հաշվեկշռին: Գնված ժամանակը գործող է 3 ամիս:
          </Typography>
          <Grid container spacing={2} sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
            {oneTimePurchases.map((option, idx) => (
              <Grid item xs={12} sm={4} key={idx} sx={{ display: 'flex' }}>
                <Paper
                  elevation={option.highlighted ? 6 : 1}
                  sx={{
                    position: 'relative',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    border: option.highlighted ? 2 : 1,
                    borderColor: option.highlighted ? 'primary.main' : 'grey.200',
                    borderRadius: 2,
                    width: '100%',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  {option.highlighted && (
                    <Chip
                      label="Առաջարկ"
                      color="success"
                      size="small"
                      sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                    />
                  )}
                  <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                    {option.label}
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 0.5 }} fontWeight="bold">
                    ${option.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Միանվագ գնում
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic', fontSize: '0.75rem' }}>
                    Գործում է 3 ամիս
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 1.5,
                      p: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Վերծանման ժամանակ
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {option.hours} ժամ
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="medium"
                    fullWidth
                    onClick={() => handleOneTimePurchase(option.id)}
                    disabled={oneTimePurchaseLoading === option.id}
                    sx={{ mt: 'auto', py: 1, borderRadius: 2 }}
                  >
                    {oneTimePurchaseLoading === option.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Գնել'
                    )}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 3, pb: 2 }}>
          <Button onClick={() => setBuyTimeDialogOpen(false)}>
            Փակել
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={buyGenerationsDialogOpen}
        onClose={() => setBuyGenerationsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          <Typography variant="h5" fontWeight="bold">
            Գնել TTS կրեդիտներ
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            px: 3
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexShrink: 0 }}>
            Գնեք լրացուցիչ TTS կրեդիտներ, որոնք կավելացվեն ձեր ընթացիկ հաշվեկշռին:
          </Typography>
          <Grid container spacing={2} sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
            {oneTimeTTSPurchases.map((option, idx) => (
              <Grid item xs={12} sm={4} key={idx} sx={{ display: 'flex' }}>
                <Paper
                  elevation={option.highlighted ? 6 : 1}
                  sx={{
                    position: 'relative',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    border: option.highlighted ? 2 : 1,
                    borderColor: option.highlighted ? 'primary.main' : 'grey.200',
                    borderRadius: 2,
                    width: '100%',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  {option.highlighted && (
                    <Chip
                      label="Առաջարկ"
                      color="success"
                      size="small"
                      sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                    />
                  )}
                  <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                    {option.label}
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 0.5 }} fontWeight="bold">
                    ${option.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Միանվագ գնում
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic', fontSize: '0.75rem' }}>
                    Գործում է 2 ամիս
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 1.5,
                      p: 1,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Քանակ
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {option.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="medium"
                    fullWidth
                    onClick={() => handleOneTimePurchase(option.id)}
                    disabled={oneTimePurchaseLoading === option.id}
                    sx={{ mt: 'auto', py: 1, borderRadius: 2 }}
                  >
                    {oneTimePurchaseLoading === option.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Գնել'
                    )}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 3, pb: 2 }}>
          <Button onClick={() => setBuyGenerationsDialogOpen(false)}>
            Փակել
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;
